import axios, { AxiosProgressEvent } from "axios";
import { refreshToken } from "../api";
import { translateKnownApiError } from "@/utils/errorHandling";
import {
  AiCredential,
  AiModel,
  AiProvider,
  Dashboard,
  DashboardQueryResult,
  DatasetDetail,
  DatasetPage,
  DatasetSummary,
  FilterSpec,
} from "@/types/analyticsPro";

// O yuha-ai é outro serviço (sem prefixo /api). Mesmo login do yuha-back: o
// cookie de sessão vai junto (withCredentials).
const getAiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  if (hostname === "dev.yuha.com.br" || hostname === "localhost" || hostname.includes("dev")) {
    return "https://dev.ai.yuha.com.br";
  }
  return "https://ai.yuha.com.br";
};

// Limite de envio do yuha-ai (decimal: 50 MB = 50.000.000 bytes).
export const PRO_MAX_FILE_BYTES = 50_000_000;

const aiApi = axios.create({
  baseURL: getAiBaseUrl(),
  withCredentials: true,
  // Sem Accept explícito a API pode responder em XML.
  headers: { Accept: "application/json" },
  timeout: 60000,
});

// Sessão expirada: renova pelo yuha-back e repete a requisição uma vez.
aiApi.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  if (error.response?.status === 401 && config && !config._retry) {
    config._retry = true;
    if (await refreshToken()) return aiApi.request(config);
    window.location.href = "/signin/";
  }
  return Promise.reject(error);
});

// Este cliente não exibe toasts: as telas usam esta função para montar a
// mensagem (pt-BR) do único toast da ação.
export const getProErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  const raw: string | undefined = data?.fields?.[0]?.message ?? data?.message;
  return (raw && translateKnownApiError(raw)) || fallback;
};

export const isProNotFound = (error: any): boolean => error?.response?.status === 404;

// ---------- análises (datasets) ----------

// Requisições em andamento: quem pedir o mesmo dado reaproveita a mesma chamada.
const pending = new Map<string, Promise<unknown>>();

const dedupe = <T>(key: string, request: () => Promise<T>): Promise<T> => {
  const current = pending.get(key) as Promise<T> | undefined;
  if (current) return current;
  const created = request().finally(() => pending.delete(key));
  pending.set(key, created);
  return created;
};

export const listProDatasets = (page: number): Promise<DatasetPage> =>
  dedupe(`list:${page}`, async () => (await aiApi.get<DatasetPage>("/datasets", { params: { page, limit: 10 } })).data);

export const getProDataset = (id: string): Promise<DatasetDetail> =>
  dedupe(`dataset:${id}`, async () => (await aiApi.get<DatasetDetail>(`/datasets/${id}`)).data);

export const uploadProDataset = async (
  file: File,
  name: string,
  onProgress?: (percent: number) => void,
): Promise<DatasetSummary> => {
  const form = new FormData();
  form.append("file", file);
  if (name.trim()) form.append("name", name.trim());
  const response = await aiApi.post<DatasetSummary>("/datasets", form, {
    // Envio de até 50 MB: sem o limite de 60 s das outras chamadas.
    timeout: 0,
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  return response.data;
};

export const deleteProDataset = async (id: string): Promise<void> => {
  await aiApi.delete(`/datasets/${id}`);
};

// 409 = já está em processamento: para a tela, é o mesmo que ter dado certo.
export const reanalyzeProDataset = async (id: string): Promise<void> => {
  try {
    await aiApi.post(`/datasets/${id}/reanalyze`);
  } catch (error: any) {
    if (error?.response?.status !== 409) throw error;
  }
};

// ---------- painel ----------

// null = a análise ainda não gerou painel (404).
export const getProDashboard = (datasetId: string): Promise<Dashboard | null> =>
  dedupe(`dashboard:${datasetId}`, async () => {
    try {
      return (await aiApi.get<Dashboard>(`/datasets/${datasetId}/dashboard`)).data;
    } catch (error) {
      if (isProNotFound(error)) return null;
      throw error;
    }
  });

export const queryProDashboard = async (
  datasetId: string,
  filters: FilterSpec[],
  signal?: AbortSignal,
): Promise<DashboardQueryResult> =>
  (await aiApi.post<DashboardQueryResult>(`/datasets/${datasetId}/dashboard/query`, { filters }, { signal })).data;

// ---------- IA ----------

export const listAiCredentials = async (): Promise<AiCredential[]> =>
  (await aiApi.get<{ data: AiCredential[] }>("/ai/credentials")).data.data;

export const listAiModels = async (provider: AiProvider, apiKey: string): Promise<AiModel[]> =>
  (await aiApi.post<{ data: AiModel[] }>("/ai/models", { provider, apiKey })).data.data;

export const saveAiCredential = async (
  provider: AiProvider,
  apiKey: string,
  preferredModel?: string,
): Promise<AiCredential> => {
  // Modelo vazio = padrão do provedor: o campo não vai no corpo.
  const body = preferredModel ? { provider, apiKey, preferredModel } : { provider, apiKey };
  return (await aiApi.post<AiCredential>("/ai/credentials", body)).data;
};

// 404 = a chave já não existia: o resultado é o mesmo.
export const deleteAiCredential = async (provider: AiProvider): Promise<void> => {
  try {
    await aiApi.delete(`/ai/credentials/${provider}`);
  } catch (error) {
    if (!isProNotFound(error)) throw error;
  }
};
